<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 id="whole-genome-sequencing-test-title">{{ testName }}</h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <v-form
        ref="form"
        v-model="valid"
        lazy-validation
        class="grid-container"
        id="whole-genome-sequencing-test-grid"
      >
        <v-container>
          <v-row>
            <v-col class="custom-col">
              <p>
                <a
                  href="https://www.vcgs.org.au/order/tests/1367"
                  target="_blank"
                  >Whole genome sequencing</a
                >
                is used to investigate complex health and developmental
                problems. It's often used by specialist groups, such as
                geneticists and neurologists, to investigate specific causes of
                well-known, but poorly understood conditions (like intellectual
                disability or brain malformations). Analysis of whole genome
                sequencing data includes the detection of deletions or
                duplications, also known as copy number variants (CNV).
              </p>
              <p>
                Patient to be referred to clinical genetics or a relevant
                specialist for testing.
              </p>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Funding source*</span>
              <v-select
                item-disabled=""
                v-model="billTo"
                :items="billingOptions"
                item-text="key"
                item-value="value"
                :id="testId + '.billTo'"
                v-on:change="billingSelection"
                outlined
                :rules="billingRule"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Turn around time*</span>
              <v-select
                v-model="urgency"
                :items="urgencyTypes"
                :id="testId + '.urgency'"
                no-data-text="Select a funding source first"
                :disabled="urgencyDisabled"
                v-on:change="familyType = ''"
                outlined
                :rules="ruleNotNull"
                hide-details="auto"
              ></v-select>
            </v-col>

            <v-col class="custom-col">
              <span class="text-title">Family type*</span>
              <v-select
                v-model="familyType"
                :items="familyTypes"
                :id="testId + '.familyType'"
                no-data-text="Select a turn around time first"
                :rules="ruleNotNull"
                v-on:change="analysisType = ''"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
            <v-col class="custom-col">
              <span class="text-title">Analysis type*</span>
              <v-select
                v-model="analysisType"
                :items="analysisTypes"
                :id="testId + '.analysisType'"
                no-data-text="Select a family type first"
                :rules="ruleNotNull"
                outlined
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>
          <div
            v-if="
              analysisType ===
                'Comprehensive genome (> 400 genes +/- Mendeliome)'
            "
            class="mt-8"
          >
            <span class="text-title"
              >Include analysis of whole mitochondrial genome (mtDNA)?</span
            >
            <v-select
              v-model="mtDna"
              :items="yesNoOptions"
              item-text="key"
              item-value="value"
              :id="testId + '.mtDNA'"
              class="pt-4"
              outlined
              hide-details="auto"
            ></v-select>
          </div>

          <v-row>
            <v-col class="custom-col">
              <v-alert text class="text-grey border-grey pl-10">
                <b>Turnaround time options:</b>
                <ul>
                  <li>Rapid (+$1300): 15 business days</li>
                  <li>Standard trio (focused or comprehensive): 8-10 weeks</li>
                  <li>Standard focused singleton: 3-4 months</li>
                  <li>Standard comprehensive singleton: 4-6 months</li>
                </ul>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col
              ><b class="text-title">Clinical features</b>
              <template v-if="brokenPhenotypesApi">
                <BrokenApiAlert type="phenotypes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="phenotypes"
                  :id="testId + '.phenotypes'"
                  :items="phenotypeList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputPhenotypes = null"
                  :search-input.sync="searchInputPhenotypes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handlePhSearchChange"
                  :filter="filterPhItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingHp">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ phSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please start typing the main clinical features. e.g.
                  intellectual disability. Select the most appropriate HPO term
                  from those suggested. Detailed clinical information helps us
                  diagnose patients more accurately. The phenotype's are sourced
                  from hpo.jax.org.</span
                >
              </template>
              <template v-if="badSearchQuery">
                <BadSearchQueryAlert />
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-checkbox
                v-model="congenital"
                item-text="key"
                item-value="value"
                label="Symptom onset is congenital"
                :id="testId + '.congenital'"
              >
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row v-if="congenital === false">
            <v-col class="custom-col">
              <span class="text-title">Age of onset</span>
              <v-text-field
                v-model="ageOfOnset"
                :id="testId + '.ageOfOnset'"
                outlined
                hide-details="auto"
              ></v-text-field>
              <span class="text-hint">Age in years</span>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                auto-grow
                rows="2"
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
                persistent-hint
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Family history</span>
              <v-textarea
                rows="1"
                auto-grow
                v-model="familyHx"
                :id="testId + '.familyHx'"
                outlined
                persistent-hint
                hide-details="auto"
              ></v-textarea>
              <span class="text-hint"
                >e.g. affected status of the parents, parental consanguinity,
                etc.</span
              >
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-checkbox
                v-model="suspectDiagnosis"
                :id="testId + '.suspectDiagnosis'"
                item-text="key"
                item-value="value"
                label="I strongly suspect a specific clinical diagnosis? E.g. Kabuki syndrome"
                hide-details="auto"
              >
              </v-checkbox>
            </v-col>
          </v-row>

          <v-row v-if="suspectDiagnosis">
            <v-col class="offset-1">
              <span class="text-title"
                >Please provide your top gene candidates</span
              >
              <template v-if="brokenGenesApi">
                <BrokenApiAlert type="genes" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="indGenes"
                  :id="testId + '.indGenes'"
                  :items="validGenesList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  persistent-hint
                  deletable-chips
                  @input="searchInputGenes = null"
                  :search-input.sync="searchInputGenes"
                  outlined
                  hide-details="auto"
                  @update:search-input="handleGenesSearchChange"
                  :filter="filterGenesItems"
                  counter="5"
                  :rules="ruleMaxCustomGenes"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenes">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genesSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint">Select up to 5 genes.</span>
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <b class="text-title">Gene panels</b>
              <template v-if="brokenGenePanelsApi">
                <BrokenApiAlert type="genePanels" />
              </template>
              <template v-else>
                <v-autocomplete
                  v-model="genePanels"
                  :id="testId + '.genePanels'"
                  :items="genePanelsList"
                  :menu-props="{ maxHeight: '200' }"
                  multiple
                  chips
                  item-color="blue"
                  deletable-chips
                  persistent-hint
                  @input="searchInputPanels = null"
                  :search-input.sync="searchInputPanels"
                  outlined
                  hide-details="auto"
                  @update:search-input="handleGenePanelsSearchChange"
                  :filter="filterGenePanelsItems"
                >
                  <template v-slot:prepend-item v-if="isSearchingGenePanels">
                    <v-progress-linear indeterminate></v-progress-linear>
                  </template>
                  <template v-slot:no-data>
                    <div class="px-4">{{ genePanelsSearchMsg }}</div>
                  </template>
                </v-autocomplete>
                <span class="text-hint"
                  >Please choose the most relevant gene panels to be used in
                  analysis. You can search panels using a key word. e.g.
                  congenital. The list will refine based on the word
                  entered.</span
                >
              </template>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <v-alert text color="info" outlined>
                <p>
                  Detailed information on panel content is available at
                  <a href="https://panelapp-aus.org/" target="_blank"
                    ><b style="color: #00538b">PanelApp Australia</b></a
                  >
                </p>
              </v-alert>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="familyType === trioFlag"
                style="grid-area: person1"
                title="Family member 1"
                :idPrefix="testId + '.fm0.'"
                ref="familyMember1"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="whole_genome_sequencing"
              />
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <FamilyMember
                v-if="familyType === trioFlag"
                style="grid-area: person2"
                title="Family member 2"
                :idPrefix="testId + '.fm1.'"
                ref="familyMember2"
                :patientdata="patientdata"
                :familytype="familyType"
                :populatekeys="[
                  'surname',
                  'country',
                  'unitFlat',
                  'streetAddress',
                  'suburb',
                  'postcode',
                  'state',
                ]"
                testType="whole_genome_sequencing"
              />
            </v-col>
          </v-row>
        </v-container>

        <WholeGenomeSequencingConsentContainer
          ref="wholeGenomeSequencingConsent"
          :is-trio="familyType === trioFlag"
          :force-form-dirty="forceFormDirty"
          :testId="testId"
          :testName="testName"
        ></WholeGenomeSequencingConsentContainer>

        <TestPriceContainer
          :price="price"
          :showReanalysisAlert="showReanalysisAlert"
        />
      </v-form>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        id="whole_genome_continue_section"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FamilyMember from "../FamilyMember";
import GenePanelMixin from "../../mixins/GenePanelMixin";
import PhenotypesMixin from "../../mixins/PhenotypesMixin";
import FormRulesMixin from "../../mixins/FormRulesMixin";
import GenesMixin from "../../mixins/GenesMixin";
import WholeGenomeSequencingConsentContainer from "./WholeGenomeSequencingConsentContainer";
import { EventBus } from "@/event-bus";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceContainer from "../common/TestPriceContainer.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import BrokenApiAlert from "../common/BrokenApiAlert.vue";
import BadSearchQueryAlert from "../common/BadSearchQueryAlert.vue";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
    forceFormDirty: {
      default: false,
      type: Boolean,
    },
    showReanalysisAlert: Boolean,
  },
  watch: {
    billTo() {
      if (this.billTo === "PATIENT") {
        useSectionStore().deligateBillingAddress = "PATIENT";
      } else {
        useSectionStore().deligateBillingAddress = "OTHER";
      }
    },
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
    familyType: function() {
      // When it is Trio test
      if (this.familyType === this.trioFlag) {
        // get patient data via EventBus
        EventBus.$emit("getPatientData");

        // update selectedTrioTests in App component
        EventBus.$emit("selectTrioTest", this.testName);
      } else {
        // when it is singleton test, update selectedTrioTests in App component
        EventBus.$emit("deselectTrioTest", this.testName);
      }
    },
    analysisType() {
      if (
        this.analysisType !==
        "Comprehensive genome (> 400 genes +/- Mendeliome)"
      ) {
        this.mtDna = null;
      }
    },
  },
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.wholeGenomeSequencingTestPricing = await this.execRetrieveJson(
      "json/pricing/whole_genome_sequencing_pricing.json",
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);

    EventBus.$emit("getPatientData");
    const element = document.getElementById(
      "whole-genome-sequencing-test-title",
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  },
  beforeDestroy() {
    EventBus.$emit("deselectTrioTest", this.testName);
  },
  mixins: [
    GenePanelMixin,
    FormRulesMixin,
    PhenotypesMixin,
    GenesMixin,
    RetrieveJSONMixin,
  ],

  name: "",

  components: {
    FamilyMember,
    WholeGenomeSequencingConsentContainer,
    TestPriceContainer,
    BrokenApiAlert,
    BadSearchQueryAlert,
  },

  data: () => ({
    valid: false,
    urgent: false,
    urgentReason: "",
    wholeGenomeSequencingTestPricing: null,
    suspectDiagnosis: "",
    congenital: false,
    urgencyTypes: [],
    genePanels: [],
    phenotypes: [],
    familyHx: "",
    clinicalNotes: "",
    ageOfOnset: "",
    urgency: "",
    urgencyDisabled: false,
    familyType: "",
    trioFlag: "Trio",
    analysisType: "",
    indGenes: [],
    billTo: "",
    mtDna: null,
    yesNoOptions: [
      { key: "Yes", value: true },
      { key: "No", value: false },
    ],
    billingOptions: [
      { key: "Patient / Family", value: "PATIENT" },
      {
        key: "Clinical Service / Institution / Hospital",
        value: "PRACTITIONER",
      },
    ],
    searchInputPanels: null,
    searchInputGenes: null,
    searchInputPhenotypes: null,
  }),

  computed: {
    billingRule() {
      return this.ruleNotNull;
    },
    familyTypes() {
      return this.urgency === ""
        ? []
        : Object.keys(this.wholeGenomeSequencingTestPricing[this.urgency]);
    },
    analysisTypes() {
      return this.urgency === "" || this.familyType === ""
        ? []
        : Object.keys(
            this.wholeGenomeSequencingTestPricing[this.urgency][
              this.familyType
            ],
          );
    },
    getPriceDetails() {
      return this.urgency === "" ||
        this.familyType === "" ||
        this.analysisType === ""
        ? null
        : this.wholeGenomeSequencingTestPricing[this.urgency][this.familyType][
            this.analysisType
          ];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.price;
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
  },

  methods: {
    billingSelection: function() {
      this.urgencyDisabled = false;
      this.urgencyTypes = Object.keys(this.wholeGenomeSequencingTestPricing);
      this.urgency = this.urgencyTypes[0];

      // When the analysis type is Comprehensive genome, the family member details will not be
      // lost in Trio test if user changes the `Funding source` option.
      if (
        this.analysisType !==
        "Comprehensive genome (> 400 genes +/- Mendeliome)"
      ) {
        this.familyType = "";
        this.analysisType = "";
      }
    },
    isFormValid: function() {
      let isValid;
      if (typeof this.$refs.wholeGenomeSequencingConsent !== "undefined") {
        isValid = this.$refs.wholeGenomeSequencingConsent.isFormValid();
      } else isValid = false;
      if (this.familyType === this.trioFlag) {
        return (
          isValid &&
          this.$refs.familyMember1.isFormValid() &&
          this.$refs.familyMember2.isFormValid()
        );
      } else return isValid;
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 1000);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
    transferData: function() {
      let familyDetails = [];
      if (this.familyType === "Trio") {
        for (let [familyKey, familyComponent] of Object.entries(this.$refs)) {
          if (
            familyComponent &&
            familyKey.substring(0, 12) === "familyMember"
          ) {
            familyDetails.push(familyComponent.transferData());
          }
        }
      }

      let result = {
        urgent: this.urgency === "Rapid",
        urgency: this.urgency,
        urgentReason: this.urgency === "Rapid" ? "Rapid genome testing" : "",
        testType: this.testId,
        familyType: this.familyType,
        analysisType: this.analysisType,
        rapid: this.rapidTest,
        genePanels: this.getFinalGenePanels(this.genePanels),
        indGenes: this.getFinalIndGenes(this.indGenes),
        phenotypes: this.getFinalPhenotypes(this.phenotypes),
        ageOfOnset: this.congenital === true ? "Congenital" : this.ageOfOnset,
        familyHx: this.familyHx,
        clinicalNotes: this.clinicalNotes,
        family: familyDetails,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        billTo: this.billTo,
        mtDna: this.mtDna,
      };

      // Consent data is present
      if (this.$refs.wholeGenomeSequencingConsent) {
        const consentData = this.$refs.wholeGenomeSequencingConsent.transferData();

        // assign the data fields from consent direct into the top level of our data
        Object.assign(result, consentData);
      }

      return result;
    },
  },
};
</script>
